import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Skull, CalendarDays, LayoutGrid, LogOut, Languages, Plus, BarChart3, Settings, Shield } from "lucide-react";
import InstallApp from "./InstallApp";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 text-sm label-caps transition-colors ${
      isActive ? "text-primary" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-white shrink-0"
        >
          <Skull className="text-primary" size={24} />
          <span className="heading text-lg sm:text-2xl font-bold">MetalVault</span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-directory">
            <LayoutGrid size={16} /> {t("directory")}
          </NavLink>
          <NavLink to="/calendar" className={linkClass} data-testid="nav-calendar">
            <CalendarDays size={16} /> {t("calendar")}
          </NavLink>
          <NavLink to="/stats" className={linkClass} data-testid="nav-stats">
            <BarChart3 size={16} /> {t("stats")}
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <Shield size={16} /> {t("admin")}
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <InstallApp />
          <button
            data-testid="add-band-btn"
            onClick={() => navigate("/bands/new")}
            className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm label-caps text-primary border border-primary/40 rounded-sm hover:bg-primary/10 transition-colors"
          >
            <Plus size={16} /> {t("add_band")}
          </button>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm label-caps text-zinc-400 hover:text-white"
          >
            <Languages size={16} /> {lang.toUpperCase()}
          </button>
          <button
            data-testid="nav-settings"
            onClick={() => navigate("/settings")}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
            title={t("settings")}
          >
            <Settings size={18} />
          </button>
          {user && (
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 text-zinc-400 hover:text-destructive transition-colors"
              title={t("logout")}
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </div>

      <nav className="md:hidden flex items-center justify-start sm:justify-around overflow-x-auto border-t border-white/10 bg-black/60 [&::-webkit-scrollbar]:hidden">
        <NavLink to="/dashboard" className={linkClass + " shrink-0"} data-testid="nav-directory-mobile">
          <LayoutGrid size={16} /> {t("directory")}
        </NavLink>
        <NavLink to="/calendar" className={linkClass + " shrink-0"} data-testid="nav-calendar-mobile">
          <CalendarDays size={16} /> {t("calendar")}
        </NavLink>
        <NavLink to="/stats" className={linkClass + " shrink-0"} data-testid="nav-stats-mobile">
          <BarChart3 size={16} /> {t("stats")}
        </NavLink>
        <NavLink to="/settings" className={linkClass + " shrink-0"} data-testid="nav-settings-mobile">
          <Settings size={16} /> {t("settings")}
        </NavLink>
        {user?.role === "admin" && (
          <NavLink to="/admin" className={linkClass + " shrink-0"} data-testid="nav-admin-mobile">
            <Shield size={16} /> {t("admin")}
          </NavLink>
        )}
        <NavLink to="/bands/new" className={linkClass + " shrink-0"} data-testid="nav-add-mobile">
          <Plus size={16} /> {t("add_band")}
        </NavLink>
      </nav>
    </header>
  );
}
